import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Calendar } from "lucide-react";
import { HERO } from "@/constants/testIds";

const HERO_BG = "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=2400&q=80";
const OMNI_BADGE = "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/b35qdpuq_Omni%20Certified%20Installer%20Logo%20%281%29.png";

const headline = ["Light it once.", "Enjoy it", "forever."];

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ onQuote, onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid={HERO.root}
      className="relative min-h-[100svh] overflow-hidden hero-vignette"
    >
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0"
      >
        <img
          src={HERO_BG}
          alt="Modern home with architectural lighting"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-[#05070A]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,7,10,0.6)_70%,#05070A_100%)]" />
      </motion.div>

      {/* Subtle particles / horizontal light lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-0 right-0 top-1/3 h-px bg-gradient-to-r from-transparent via-[color:var(--primary)]/40 to-transparent" />
        <div className="absolute left-0 right-0 bottom-1/3 h-px bg-gradient-to-r from-transparent via-[color:var(--primary)]/25 to-transparent" />
      </div>

      <motion.div style={{ opacity }} className="relative z-10 container-x pt-64 md:pt-80 lg:pt-[22rem] xl:pt-[24rem] pb-24 min-h-[100svh] flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-8 h-px bg-[color:var(--primary)]" />
          <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-[color:var(--primary)]">
            Servicing Wisconsin & Southwest Florida
          </span>
        </motion.div>

        <h1
          data-testid={HERO.headline}
          className="font-display uppercase text-white leading-[0.88] text-[clamp(3.2rem,10.5vw,10rem)] tracking-tight"
        >
          {headline.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                className="line-inner block"
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                custom={i}
              >
                {i === 1 ? (
                  <>
                    Enjoy it{" "}
                    <span className="text-[color:var(--primary)]">·</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          className="mt-12 grid md:grid-cols-[1fr_auto] items-end gap-8 md:gap-16"
        >
          <p className="max-w-xl text-white/70 text-base md:text-lg leading-relaxed">
            Permanent architectural lighting and Omni RGB installations for homes and businesses across Wisconsin and Southwest Florida.
            Engineered once. Programmed for every season. No more ladders — ever again.
          </p>

          <div className="flex flex-wrap gap-3">
            <button
              data-testid={HERO.quoteCta}
              onClick={onQuote}
              className="group inline-flex items-center gap-2 px-6 py-3.5 bg-[color:var(--primary)] text-black font-mono-accent text-[11px] tracking-[0.25em] uppercase hover:bg-white transition-colors btn-sharp"
            >
              Request a Quote
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <button
              data-testid={HERO.bookCta}
              onClick={onBook}
              className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/25 text-white font-mono-accent text-[11px] tracking-[0.25em] uppercase hover:border-[color:var(--primary)] hover:text-[color:var(--primary)] transition-colors btn-sharp"
            >
              <Calendar size={14} />
              Book Consultation
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono-accent text-[10px] tracking-[0.35em] uppercase text-white/50 flex flex-col items-center gap-2"
      >
        Scroll
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="w-px h-6 bg-white/40"
        />
      </motion.div>
    </section>
  );
}
